import { useMemo } from "react"

import { useTranslation } from "react-i18next"
import { useParams } from "react-router-dom"

import ArrowForwardOutlinedIcon from "@mui/icons-material/ArrowForwardOutlined"
import { CircularProgress, Grid, Popover } from "@mui/material"
import { Button, Tag, Typography } from "@synapse-analytics/synapse-ui"

import { KonanAvatarComponent } from "../../../../components/core/KonanAvatar"
import { KonanEmptyState } from "../../../../components/utility/KonanEmptyState"
import type { ActualDecision } from "../../../../types/generated/api/ActualDecision"
import { PredictionReview } from "../../../../types/generated/api/PredictionReview"
import { useActualDecisionHistory } from "../../hooks/useActualDecisionHistory"
import { usePredictionReviewHistory } from "../../hooks/usePredictionReviewHistory"
import type { HistoryLogProps } from "../../interfaces"

/**
 * Popover rendering either prediction review history or actual decision history.
 */
export function HistoryLog(props: Readonly<HistoryLogProps>): React.ReactElement {
  const { isOpen, onClose, anchorEl, id, predictionId, variant, enabled } = props

  const { id: projectId } = useParams<{ id: string }>()

  const { t } = useTranslation("decisions", {
    keyPrefix: "projectDecisions",
  })

  const {
    isLoading,
    data: history,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage,
  } = usePredictionReviewHistory(predictionId, enabled && variant === "review")

  const {
    data: actualDecisionRes,
    isFetchingNextPage: isFetchingNextActualDecisionsPage,
    fetchNextPage: fetchNextActualDecisionsPage,
    hasNextPage: hasNextActualDecisionPage,
    isFetching: isFetchingActualDecision,
  } = useActualDecisionHistory({
    projectId: projectId as string,
    predictionId,
    enabled: !!predictionId && enabled && variant === "actual-decision",
  })

  // extracting the results after infinite-Query
  const adjustedHistory = useMemo(() => {
    let result

    if (variant === "review") {
      result = history?.pages.reduce((accumulator, page) => {
        return accumulator.concat(page.data.results ?? [])
      }, [] as Array<PredictionReview>)
    }

    if (variant === "actual-decision") {
      result = actualDecisionRes?.pages.reduce((accumulator, page) => {
        return accumulator.concat(page.data.results ?? [])
      }, [] as Array<ActualDecision>)
    }

    return result
  }, [history?.pages, actualDecisionRes?.pages, variant])

  return (
    <Popover
      id={id}
      open={isOpen}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "left",
      }}
      slotProps={{
        paper: {
          id: "review-history-scrollable-target",
          className: "verticalScroller",
          style: {
            borderRadius: "8px",
            boxShadow: "var(--box-shadow)",
            minWidth: 250,
            maxWidth: 400,
            maxHeight: 200,
            height: "auto",
            backgroundColor: "var(--base-background-3)",
            border: "1px solid var(--base-border-1)",
          },
        },
      }}
    >
      <Grid container p={2}>
        {isLoading || isFetchingActualDecision ? (
          <Grid container item xs={12} display={"flex"} justifyContent={"center"} alignContent={"center"}>
            <CircularProgress size={20} style={{ color: "var(--blue-text-2)" }} />
          </Grid>
        ) : adjustedHistory?.length === 0 ? (
          <Grid item mb={2.5}>
            <KonanEmptyState
              title={t("messages.reviewHistoryUnavailable")}
              subTitle={variant === "review" ? t("messages.markAsReviewed") : t("messages.pickActualDecision")}
            />
          </Grid>
        ) : (
          <Grid container item xs={12} gap={variant === "actual-decision" ? 3 : 1.5}>
            {adjustedHistory &&
              adjustedHistory?.length > 0 &&
              adjustedHistory.map((reviewLog) => (
                <Grid
                  container
                  display={variant === "actual-decision" ? "block" : "flex"}
                  justifyContent={"space-between"}
                  key={reviewLog.uuid}
                  flexWrap={"nowrap"}
                >
                  <Grid item xs={variant === "actual-decision" ? 12 : 9}>
                    <KonanAvatarComponent
                      createdBy={reviewLog.created_by}
                      title={reviewLog.created_by}
                      createdAt={reviewLog.created_at}
                      avatarName={reviewLog.created_by}
                    />
                  </Grid>

                  <Grid item display={"flex"} justifyContent={"start"} alignItems={"center"} mt={1}>
                    {"label" in reviewLog ? (
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                        }}
                      >
                        <Typography variant="label-regular" variantColor={2}>
                          <ArrowForwardOutlinedIcon
                            style={{ display: "block", width: "12px", height: "12px", marginInlineEnd: "0.5rem" }}
                          />
                        </Typography>
                        <Typography variant="p" variantColor={2}>
                          {reviewLog.label?.name ?? t("status.notProvided")}
                        </Typography>
                      </div>
                    ) : (
                      <Tag
                        style={{ textTransform: "initial" }}
                        variant="filled"
                        color={reviewLog.status === PredictionReview.status.REVOKED ? "gray" : "green"}
                      >
                        {reviewLog.status?.toLowerCase()}
                      </Tag>
                    )}
                  </Grid>
                </Grid>
              ))}

            {((variant === "review" && hasNextPage) ||
              (variant === "actual-decision" && hasNextActualDecisionPage)) && (
              <Grid item display={"flex"}>
                <Button
                  variant="ghost"
                  onClick={() => {
                    if (variant === "review" && !isFetchingNextPage) {
                      fetchNextPage()
                    }

                    if (variant === "actual-decision" && !isFetchingNextActualDecisionsPage) {
                      fetchNextActualDecisionsPage()
                    }
                  }}
                >
                  <Grid container item xs={12} display={"flex"}>
                    {t("loadMore")}
                    {((variant === "review" && isFetchingNextPage) ||
                      (variant === "actual-decision" && isFetchingNextActualDecisionsPage)) && (
                      <Grid item ml={1} display={"flex"} alignItems={"center"} justifyContent={"center"}>
                        <CircularProgress size={14} color="primary" />
                      </Grid>
                    )}
                  </Grid>
                </Button>
              </Grid>
            )}
          </Grid>
        )}
      </Grid>
    </Popover>
  )
}
